import { Link } from "react-router-dom";
import { Check, Sparkles, Camera, MessageCircle, LineChart, Wrench, BrushCleaning } from "lucide-react";

const SERVICES = [
    {
        id: "management",
        icon: Sparkles,
        title: "Airbnb Management",
        kicker: "End-to-end hosting",
        desc: "Complete short-stay management for Melbourne owners across the west and Geelong. From listing setup to nightly hospitality, we run your property to a hotel standard — without you lifting a finger.",
        benefits: [
            "Multi-platform listing (Airbnb, VRBO, Booking.com)",
            "Single point of contact",
            "Monthly performance reporting",
            "Transparent, performance-based fee",
        ],
        image:
            "https://images.unsplash.com/photo-1749181060201-430390bd1655?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtZWxib3VybmUlMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzg5MzAyNzl8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "styling",
        icon: Wrench,
        title: "Styling & Furnishing",
        kicker: "Hosting-ready interiors",
        desc: "We help take your property from empty (or tired) to listing-ready — selecting furniture, soft furnishings and finishing touches that look great in photos and feel great for guests.",
        benefits: [
            "Furniture & soft-furnishing recommendations",
            "Art, lighting & finishing touches",
            "Hotel-grade linen & amenities",
            "Coordinated setup so you can launch quickly",
        ],
        image:
            "https://images.unsplash.com/photo-1683629357963-adf2b1fa9ad9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxtYXJibGUlMjBraXRjaGVuJTIwbHV4dXJ5JTIwaG9tZXxlbnwwfHx8fDE3Nzg5MzAyNzl8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "cleaning",
        icon: BrushCleaning,
        title: "Airbnb Cleaning",
        kicker: "Hotel-standard turnovers",
        desc: "Our cleaning team turns the property over between every stay — fresh linen, restocked toiletries and consumables, and a tidy presentation that earns five-star reviews.",
        benefits: [
            "Linen & toiletries restocked every stay",
            "Visual check after every clean",
            "Damage & inventory notes",
            "Eco-friendly products on request",
        ],
        image:
            "https://images.unsplash.com/photo-1686023858216-4f54c853acf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtYXJibGUlMjBraXRjaGVuJTIwbHV4dXJ5JTIwaG9tZXxlbnwwfHx8fDE3Nzg5MzAyNzl8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "photography",
        icon: Camera,
        title: "Airbnb Photography",
        kicker: "Professional listing photos",
        desc: "Good photos are what get your property booked. We arrange professional real-estate and hotel-standard photography that shows the home at its best.",
        benefits: [
            "Wide-angle, well-lit interior shots",
            "Exterior and key feature photos",
            "Light styling on the day",
            "Edited images ready to upload",
        ],
        image:
            "https://images.unsplash.com/photo-1664908790579-34b71154f603?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4OTMwMzAxfDA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "guest",
        icon: MessageCircle,
        title: "Guest Communication",
        kicker: "Responsive hospitality",
        desc: "Every guest is screened and supported throughout their stay. We aim to reply within an hour, sort out any issues on your behalf, and request reviews after check-out.",
        benefits: [
            "Guest screening on every booking",
            "Fast inbox responses (target: under 1 hour)",
            "On-call support during stays",
            "Active review management",
        ],
        image:
            "https://images.unsplash.com/photo-1680416124536-22b16c411402?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiZWRyb29tJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBtZWxib3VybmV8ZW58MHx8fHwxNzc4OTMwMjkyfDA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "pricing",
        icon: LineChart,
        title: "Listing & Pricing Optimisation",
        kicker: "Data-informed pricing",
        desc: "Flat pricing leaves money on the table. We use dynamic pricing tools (PriceLabs) and review market activity regularly so your nightly rate stays competitive across the seasons.",
        benefits: [
            "Dynamic pricing via PriceLabs",
            "Event-aware rates (AFL, F1, school holidays)",
            "Listing copy & SEO review",
            "Hero-image and title testing",
        ],
        image:
            "https://images.unsplash.com/photo-1649731000184-7ced04998f44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc4OTMwMzAxfDA&ixlib=rb-4.1.0&q=85",
    },
];

export default function Services() {
    return (
        <div className="bg-night">
            {/* Header */}
            <section className="pt-40 pb-20 md:pt-48 md:pb-28" data-testid="services-hero">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-6">Services</div>
                            <h1 className="headline text-5xl md:text-7xl lg:text-[88px] text-cream leading-[1.02]">
                                Every detail<br />
                                <span className="italic text-gold">handled with care.</span>
                            </h1>
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                            <p className="text-cream/55 font-light text-lg leading-relaxed">
                                Six services — available individually or together
                                as part of full-service management.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Service blocks */}
            {SERVICES.map((s, i) => (
                <section
                    key={s.id}
                    id={s.id}
                    className={`py-20 md:py-28 ${
                        i % 2 === 0 ? "bg-night" : "bg-surface"
                    }`}
                    data-testid={`service-section-${s.id}`}
                >
                    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                        <div
                            className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${
                                i % 2 === 1 ? "lg:[direction:rtl]" : ""
                            }`}
                        >
                            <div
                                className="lg:col-span-6 reveal [direction:ltr]"
                                data-testid={`service-image-${s.id}`}
                            >
                                <div className="editorial-img aspect-[5/6] overflow-hidden border border-line-dark">
                                    <img
                                        src={s.image}
                                        alt={s.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-6 reveal [direction:ltr] lg:pl-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <s.icon size={28} strokeWidth={1} className="text-gold" />
                                    <span className="eyebrow">{s.kicker}</span>
                                </div>
                                <h2 className="headline text-4xl md:text-6xl text-cream mb-6">
                                    {s.title}
                                </h2>
                                <p className="text-cream/55 font-light text-lg leading-relaxed mb-8 max-w-xl">
                                    {s.desc}
                                </p>
                                <ul className="space-y-3 mb-10">
                                    {s.benefits.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-3 text-cream"
                                        >
                                            <Check
                                                size={16}
                                                strokeWidth={1.5}
                                                className="text-gold mt-1 shrink-0"
                                            />
                                            <span className="font-light">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contact"
                                    className="btn-gold inline-block"
                                    data-testid={`service-cta-${s.id}`}
                                >
                                    Enquire about {s.title.split(" ")[0]}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* Closing CTA */}
            <section className="py-24 md:py-32 bg-[#0A0A0A] text-cream relative overflow-hidden">
                <div className="grain absolute inset-0" />
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <div className="eyebrow text-gold mb-6">All services. One partner.</div>
                    <h2 className="headline text-4xl md:text-6xl mb-8">
                        Ready to talk through<br />
                        <span className="italic text-gold">your property?</span>
                    </h2>
                    <Link to="/contact" className="btn-gold inline-block" data-testid="services-bottom-cta">
                        Book Free Consultation
                    </Link>
                </div>
            </section>
        </div>
    );
}
